import React from "react";
import styled, { css, RuleSet } from "styled-components";
import { Badge, FloatButton, Tooltip } from "antd";

import {
  AI_GRADIENT,
  compassLightGreen,
  darkGreen,
  lightGreyOutline,
} from "../../styles";

export type CompassFloatButtonVariant = "ai" | "primary" | "neutral";

const EASE = "cubic-bezier(0.2, 0.8, 0.2, 1)";

const RING: Record<CompassFloatButtonVariant, string> = {
  ai: AI_GRADIENT,
  primary: `linear-gradient(${compassLightGreen}, ${compassLightGreen})`,
  neutral: `linear-gradient(${lightGreyOutline}, ${lightGreyOutline})`,
};

const GLOW: Record<CompassFloatButtonVariant, string> = {
  ai: "rgba(43, 179, 243, 0.22)",
  primary: "rgba(32, 226, 144, 0.22)",
  neutral: "rgba(21, 50, 48, 0.12)",
};

const Fixed = styled.div<{ $right: number; $bottom: number }>`
  position: fixed;
  right: ${({ $right }): number => $right}px;
  bottom: ${({ $bottom }): number => $bottom}px;
  z-index: 99;
`;

const Disc = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  font-size: 14px;
  color: ${darkGreen};
  transition: transform 0.35s ${EASE};
`;

const Label = styled.span`
  font-family: "ApercuMedium";
  font-size: 13px;
  color: ${darkGreen};
  white-space: nowrap;
`;

const Surface = styled.button<{
  $shape: "circle" | "square";
  $expand: boolean;
  $variant: CompassFloatButtonVariant;
}>`
  all: unset;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  border: 1.5px solid transparent;
  background:
    linear-gradient(white, white) padding-box,
    ${({ $variant }): string => RING[$variant]} border-box;
  box-shadow:
    0 1px 2px rgba(21, 50, 48, 0.06),
    0 8px 24px rgba(21, 50, 48, 0.08);
  transition:
    box-shadow 0.25s ease,
    transform 0.2s ${EASE};

  &:hover {
    transform: translateY(-1px);
    box-shadow:
      0 1px 2px rgba(21, 50, 48, 0.06),
      0 10px 28px ${({ $variant }): string => GLOW[$variant]};
  }
  &:hover ${Disc} {
    transform: rotate(-20deg);
  }
  &:active {
    transform: translateY(0);
  }
  &:focus-visible {
    outline: 2px solid ${compassLightGreen};
    outline-offset: 2px;
  }

  ${({ $shape, $expand }): RuleSet<object> =>
    $shape === "square"
      ? css`
          flex-direction: column;
          justify-content: center;
          gap: 6px;
          min-width: 64px;
          padding: 10px 12px 8px;
          border-radius: 12px;

          ${Label} {
            font-size: 11px;
          }
        `
      : css`
          height: 40px;
          padding: 0 4.5px;
          border-radius: 999px;

          ${Label} {
            display: inline-block;
            overflow: hidden;
            max-width: ${$expand ? "0px" : "none"};
            opacity: ${$expand ? 0 : 1};
            margin-left: ${$expand ? "0px" : "8px"};
            margin-right: ${$expand ? "0px" : "8px"};
            transition:
              max-width 0.28s ${EASE},
              opacity 0.18s ease,
              margin 0.28s ${EASE};
          }
          &:hover ${Label}, &:focus-visible ${Label} {
            max-width: 220px;
            opacity: 1;
            margin-left: 8px;
            margin-right: 8px;
          }
        `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    ${Disc}, ${Label} {
      transition: none;
    }
  }
`;

export const CompassFloatButton = ({
  icon,
  label,
  tooltip,
  variant = "primary",
  shape = "circle",
  expandOnHover = true,
  right = 32,
  bottom = 32,
  badge,
  onClick,
}: CompassFloatButtonProps): JSX.Element => {
  const surface = (
    <Surface
      type="button"
      $shape={shape}
      $expand={expandOnHover}
      $variant={variant}
      onClick={onClick}
      aria-label={tooltip || label}
    >
      <Disc>{icon}</Disc>
      {label && <Label>{label}</Label>}
    </Surface>
  );

  const withBadge = badge ? (
    <Badge count={badge.count} dot={badge.dot} offset={[-4, 4]}>
      {surface}
    </Badge>
  ) : (
    surface
  );

  return (
    <Fixed $right={right} $bottom={bottom}>
      {tooltip && !(shape === "circle" && expandOnHover && label) ? (
        <Tooltip title={tooltip} placement="left">
          {withBadge}
        </Tooltip>
      ) : (
        withBadge
      )}
    </Fixed>
  );
};

export type CompassFloatButtonProps = {
  icon: React.ReactNode;
  label?: string;
  tooltip?: string;
  variant?: CompassFloatButtonVariant;
  shape?: "circle" | "square";
  expandOnHover?: boolean;
  right?: number;
  bottom?: number;
  badge?: { count?: number; dot?: boolean };
  onClick?: () => void;
};

export const CompassFloatButtonGroup = styled(FloatButton.Group)<{
  $right: number;
  $bottom: number;
}>`
  right: ${({ $right }): number => $right}px;
  bottom: ${({ $bottom }): number => $bottom}px;
`;
